import React, { useCallback, useEffect, useState } from "react";
import {
  collectionGroup,
  getCountFromServer,
  query,
  where,
} from "firebase/firestore";
import { FaStream } from "react-icons/fa";
import { SectionCard, MiniStat } from "../SectionCard";
import { firestoreDev, firestoreProd } from "../../../firebase";
import { fmtN } from "../format";

const ACCENT = "#b45309";

/**
 * Loan stage history has no backend API today — the standalone export tool
 * reads `collectionGroup("history")` directly from Firestore. We mirror that
 * here with cheap count-only aggregation queries (no doc reads) for a quick
 * pulse, then launch the full export tool for the detailed CSV. Counts use the
 * same (organizationId, type) composite index the export relies on.
 */
export default function LoanStageSection({ organizationId, env }) {
  const firestore = env === "dev" ? firestoreDev : firestoreProd;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [counts, setCounts] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!firestore) throw new Error("Firestore unavailable");
      const base = collectionGroup(firestore, "history");
      const [created, changed] = await Promise.all([
        getCountFromServer(
          query(
            base,
            where("organizationId", "==", organizationId),
            where("type", "==", "loan_created"),
          ),
        ),
        getCountFromServer(
          query(
            base,
            where("organizationId", "==", organizationId),
            where("type", "==", "loan_stage_changed"),
          ),
        ),
      ]);
      setCounts({
        created: created.data().count,
        changed: changed.data().count,
      });
    } catch (err) {
      setError(err?.message || "Failed to count stage events");
    } finally {
      setLoading(false);
    }
  }, [firestore, organizationId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <SectionCard
      title="Loan Stage History"
      subtitle="Stage transitions captured for this org"
      icon={<FaStream />}
      accent={ACCENT}
      loading={loading && !counts}
      error={error}
      onRetry={load}
      size="third"
      link={{
        to: `/loan-stage-analytics?org=${encodeURIComponent(organizationId)}`,
        label: "Open stage history export",
      }}
    >
      {counts ? (
        <>
          <div className="oo-ministat-row">
            <MiniStat label="Loans created" value={fmtN(counts.created)} accent={ACCENT} />
            <MiniStat label="Stage changes" value={fmtN(counts.changed)} />
          </div>
          <div className="oo-empty-hint" style={{ padding: "4px 0", textAlign: "left" }}>
            All-time event counts. Use the export tool for a per-loan CSV with
            from/to stages and timestamps.
          </div>
        </>
      ) : null}
    </SectionCard>
  );
}
